import React, { FormEvent, LegacyRef, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAtom, useSetAtom } from "jotai";
import {
  addPrintTemplate,
  addPrintTemplateFile,
} from "features/PrintTemplates/api";
import FormData from "form-data";
import { useGetDocumentsTypes } from "shared-apis/api";

import { NamedInput } from "components/NamedInput/NamedInput";
import { BaseLayout } from "components/BaseLayout/BaseLayout";
import { ModalHeader } from "components/Modal/AppModal";
import { Button, EButtonVariant } from "components/Button/Button";
import { NamedSelect } from "components/NamedSelect/NamedSelect";
import { Divider } from "components/Divider/Divider";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";

import {
  getAndSetPrintTemplatesAtom,
  usePrintTemplatesState,
} from "../../../atoms/printTemplatesAtom";
import { setModalCloseAtom } from "../../../atoms/modalAtom/modalAtom";
import { DocumentsTypes } from "../../../types";

import styles from "../styles.module.scss";
import { AddPrintTemplateProps, AddPrintTemplateValues } from "../types";

export const AddPrintTemplate = ({
  isEdit,
}: AddPrintTemplateProps): JSX.Element => {
  const closeModal = useSetAtom(setModalCloseAtom);
  const getAndSetPrintTemplates = useSetAtom(getAndSetPrintTemplatesAtom);
  const { data: documentsTypes = [] } = useGetDocumentsTypes();

  const fileInputRef = React.createRef<HTMLInputElement>();

  const [selectedFile, setSelectedFile] = useState<File | undefined>();

  const options = useMemo(
    () =>
      (documentsTypes as DocumentsTypes[]).map((item) => ({
        label: item.name,
        value: item.operationid,
      })),
    [documentsTypes]
  );

  const { handleSubmit, control, watch } = useForm<AddPrintTemplateValues>({
    defaultValues: {
      title: "",
      doc_operation: "",
    },
  });

  const saveFile = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await addPrintTemplateFile(formData);

      if (!data?.file) return;

      return data.file;
    } catch (e) {
      console.warn(e);
    }
  };

  const handleSave = async (data: AddPrintTemplateValues) => {
    if (!data.title || !data.doc_operation || !selectedFile) return;
    const filePath = await saveFile(selectedFile);
    if (!filePath) return;

    const { data: resData } = await addPrintTemplate({
      title: data.title,
      doc_operation: data.doc_operation,
      file_path: filePath,
    });

    if (!resData) return;

    await getAndSetPrintTemplates();

    closeModal();
  };

  const onAddFileClick = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  return (
    <BaseLayout
      className={styles.wrapper}
      header={<ModalHeader title={"Новый шаблон для печати"} />}
      footer={
        <Button
          onClick={handleSubmit(handleSave)}
          variant={EButtonVariant.SECONDARY}
          icon="galochka"
        >
          Сохранить
        </Button>
      }
    >
      <form
        onSubmit={(e: FormEvent) => e.preventDefault()}
        className={styles.form}
      >
        <div>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <NamedInput
                {...field}
                isRequired
                label="Название"
                classNameContainer={styles.input_container}
              />
            )}
          />
          <Controller
            name="doc_operation"
            control={control}
            render={({ field }) => (
              <NamedSelect
                {...field}
                onChange={(e: any) => {
                  field.onChange(e.value);
                }}
                value={options?.find(
                  (item: any) => +item.value === +field.value
                )}
                options={options}
                name=""
                label="Тип документа:"
                className={styles.select}
                containerClassName={styles.select_container}
              />
            )}
          />

          <input
            ref={fileInputRef}
            type={"file"}
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(ev) =>
              ev.target.files![0] ? setSelectedFile(ev.target.files![0]) : null
            }
            style={{ display: "none" }}
          />

          <VerticalSpace size={30} />
          <Divider />
          <VerticalSpace size={30} />

          {selectedFile && (
            <p className={styles.fileName}>{selectedFile.name}</p>
          )}
          <VerticalSpace size={20} />
          <Button className={styles.button} onClick={onAddFileClick}>
            Выбрать {selectedFile && "другой "}файл
          </Button>
        </div>
      </form>
    </BaseLayout>
  );
};
